// Minimal, ephemeral event stream for dev viewing

export type StreamDirection = 'in' | 'out';
export type StreamChannel = 'room' | 'account';
export type StreamKind =
  | 'event'
  | 'command'
  | 'query'
  | 'state'
  | 'state_sync'
  | 'ack'
  | 'handshake'
  | 'error'
  | 'connection'
  | 'server'
  | 'health';

export interface StreamEntry {
  id: string;
  ts: number;
  channel: StreamChannel; // which socket the entry came through
  direction: StreamDirection; // 'in' for events, 'out' for commands/queries
  kind: StreamKind; // event | command | query
  type: string; // e.g., GameElementCreated, RoomAction
  action?: string; // e.g., START, CREATE, UPDATE
  version?: number; // For events
  raw?: unknown; // full JSON payload for expanded view
}

type Listener = (entries: StreamEntry[]) => void;

class SimpleEventStream {
  private buffer: StreamEntry[] = [];
  private listeners = new Set<Listener>();
  private max = 100;
  private seq = 0;
  private enabled = false;

  subscribe(cb: Listener) {
    this.listeners.add(cb);
    // Immediately send current buffer
    cb(this.buffer.slice());
    return () => {
      this.listeners.delete(cb);
    };
  }

  push(partial: Omit<StreamEntry, 'id' | 'ts'>) {
    if (!this.enabled) return;
    const entry: StreamEntry = {
      id: `e_${++this.seq}`,
      ts: Date.now(),
      ...partial,
    };
    this.buffer.push(entry);
    if (this.buffer.length > this.max)
      this.buffer.splice(0, this.buffer.length - this.max);
    const snapshot = this.buffer.slice();
    this.listeners.forEach((cb) => cb(snapshot));
  }

  clear() {
    this.buffer = [];
    const snapshot = this.buffer.slice();
    this.listeners.forEach((cb) => cb(snapshot));
  }

  setMax(n: number) {
    this.max = Math.max(1, n);
    if (this.buffer.length > this.max)
      this.buffer.splice(0, this.buffer.length - this.max);
  }

  getAll() {
    return this.buffer.slice();
  }

  setEnabled(value: boolean) {
    this.enabled = value;
    if (!this.enabled) {
      // Optional: drop buffer to avoid stale logs; keep it simple
      this.clear();
    }
  }

  isEnabled() {
    return this.enabled;
  }
}

export const simpleEventStream = new SimpleEventStream();
